// Client API du Cockpit Avocat — endpoints `/api/v1/avocat/*` (backend
// gsf-arbitral-core, role PARTIE). Fichier separe de `api.ts` ; reutilise
// l'`apiClient` axios (intercepteur JWT + baseURL same-origin en prod).
//
// Modele « Avocat = PARTIE » : aucun de ces appels ne transitionne un
// dossier ni n'expose de brouillon de sentence (cf. RBAC + RLS serveur).
import { apiClient } from "@/lib/api";

function ensureArray<T>(value: unknown, endpoint: string): T[] {
  if (Array.isArray(value)) return value as T[];
  // eslint-disable-next-line no-console
  console.warn(
    `[avocatApi] ${endpoint}: la reponse n'est pas un tableau (VITE_API_BASE_URL ?).`,
    { receivedType: typeof value },
  );
  return [];
}

// --- Types (miroir des schemas Pydantic backend/schemas/avocat.py) ------

export interface AvocatDossierLink {
  id: string;
  dossier_id: number;
  partie: string;
  honoraires_prevus: number | null;
  honoraires_factures: number | null;
  created_at: string;
}

export interface AvocatDashboard {
  avocat_id: string;
  total_dossiers: number;
  dossiers: AvocatDossierLink[];
}

export interface AvocatDecision {
  id: string;
  dossier_id: number;
  decision_type: string | null;
  content_path: string | null;
  notified_at: string | null;
  analyse_ia: Record<string, unknown> | null;
  delai_recours: number | null;
}

export interface AvocatConclusion {
  id: string;
  dossier_id: number;
  session_state: string;
  human_validated: boolean;
  submitted_at: string | null;
  created_at: string;
}

export interface AvocatPiece {
  id: string;
  dossier_id: number;
  filename: string;
  bordereau_number: string | null;
  statut: string;
  motif_ecart: string | null;
  classement: string | null;
  categorie?: string | null;
  created_at: string;
}

export interface ClientPortalLink {
  id: string;
  dossier_id: number;
  client_email: string;
  token: string;
  expires_at: string | null;
  created_at: string;
}

export interface AvocatAudience {
  id: string;
  dossier_id: number;
  consentement_tsa: boolean;
  regles_visio: boolean;
  signed_at: string | null;
  pv_path: string | null;
  observations_pv: string | null;
  created_at: string;
}

export interface AvocatCorrespondance {
  id: string;
  dossier_id: number;
  expediteur_id: string;
  destinataire_id: string;
  type_message: string;
  sous_type: string | null;
  statut: string;
  visibilite: string;
  content: string | null;
  thread_id: string | null;
  reply_to_id: string | null;
  horodatage: string;
  lu: boolean;
}

// --- Lectures -----------------------------------------------------------

export async function getAvocatDashboard(): Promise<AvocatDashboard> {
  const { data } = await apiClient.get<AvocatDashboard>(
    "/api/v1/avocat/dashboard",
  );
  return data;
}

export async function listAvocatDecisions(): Promise<AvocatDecision[]> {
  const { data } = await apiClient.get<AvocatDecision[]>(
    "/api/v1/avocat/decisions",
  );
  return ensureArray<AvocatDecision>(data, "listAvocatDecisions");
}

export async function listAvocatConclusions(): Promise<AvocatConclusion[]> {
  const { data } = await apiClient.get<AvocatConclusion[]>(
    "/api/v1/avocat/conclusions",
  );
  return ensureArray<AvocatConclusion>(data, "listAvocatConclusions");
}

export async function listAvocatPieces(): Promise<AvocatPiece[]> {
  const { data } = await apiClient.get<AvocatPiece[]>("/api/v1/avocat/pieces");
  return ensureArray<AvocatPiece>(data, "listAvocatPieces");
}

export async function listAvocatAudiences(): Promise<AvocatAudience[]> {
  const { data } = await apiClient.get<AvocatAudience[]>(
    "/api/v1/avocat/audience",
  );
  return ensureArray<AvocatAudience>(data, "listAvocatAudiences");
}

export async function listClientPortalLinks(): Promise<ClientPortalLink[]> {
  const { data } = await apiClient.get<ClientPortalLink[]>(
    "/api/v1/avocat/client-portal",
  );
  return ensureArray<ClientPortalLink>(data, "listClientPortalLinks");
}

export async function listAvocatCorrespondance(): Promise<
  AvocatCorrespondance[]
> {
  const { data } = await apiClient.get<AvocatCorrespondance[]>(
    "/api/v1/avocat/correspondance",
  );
  return ensureArray<AvocatCorrespondance>(data, "listAvocatCorrespondance");
}

// --- Ecritures (depot) --------------------------------------------------

export async function createAvocatConclusion(
  dossierId: number,
  draftContent?: Record<string, unknown>,
): Promise<AvocatConclusion> {
  const { data } = await apiClient.post<AvocatConclusion>(
    "/api/v1/avocat/conclusions",
    { dossier_id: dossierId, draft_content: draftContent ?? null },
  );
  return data;
}

/** Dépose (soumet) une session de conclusions : seul ce geste renseigne
 *  `submitted_at` côté backend. Voir `POST /conclusions/{id}/soumettre`. */
export async function soumettreConclusion(
  conclusionId: string,
): Promise<AvocatConclusion> {
  const { data } = await apiClient.post<AvocatConclusion>(
    `/api/v1/avocat/conclusions/${conclusionId}/soumettre`,
  );
  return data;
}

export async function createAvocatPiece(input: {
  dossier_id: number;
  filename: string;
  file_path: string;
  bordereau_number?: string | null;
}): Promise<AvocatPiece> {
  const { data } = await apiClient.post<AvocatPiece>(
    "/api/v1/avocat/pieces",
    input,
  );
  return data;
}

export async function createClientPortalLink(input: {
  dossier_id: number;
  client_email: string;
  expires_days?: number;
}): Promise<ClientPortalLink> {
  const { data } = await apiClient.post<ClientPortalLink>(
    "/api/v1/avocat/client-portal",
    input,
  );
  return data;
}

export async function signAudienceConsentement(input: {
  dossier_id: number;
  consentement_tsa: boolean;
  regles_visio: boolean;
}): Promise<AvocatAudience> {
  const { data } = await apiClient.post<AvocatAudience>(
    "/api/v1/avocat/audience/consentement",
    input,
  );
  return data;
}

export async function sendAvocatCorrespondance(input: {
  dossier_id: number;
  destinataire_id: string;
  type_message: string;
  content?: string | null;
  sous_type?: string | null;
  visibilite?: string | null;
  reply_to_id?: string | null;
}): Promise<AvocatCorrespondance> {
  const { data } = await apiClient.post<AvocatCorrespondance>(
    "/api/v1/avocat/correspondance",
    input,
  );
  return data;
}

export async function transitionAvocatCorrespondance(
  messageId: string,
  statut: string,
): Promise<AvocatCorrespondance> {
  const { data } = await apiClient.post<AvocatCorrespondance>(
    `/api/v1/avocat/correspondance/${messageId}/statut`,
    { statut },
  );
  return data;
}

// --- Phase 3 : création / dépôt d'un dossier d'arbitrage -------------------

// `Qualite` = partie représentée par l'avocat (mappée à l'enum DB PartieDossier).
export type Qualite = "DEMANDEUR" | "DEFENDEUR";

// Typologie des parties au dossier (stockée en JSONB côté backend, pas un enum DB) :
// inclut les tiers intervenants (volontaire / forcé).
export type PartieQualite = Qualite | "TIERS_VOLONTAIRE" | "TIERS_FORCE";

export interface PartieInput {
  nom: string;
  qualite: PartieQualite;
  adresse: string;
  domicile_elu?: string | null;
}

export interface ArbitreProposeInput {
  nom: string;
  commentaire?: string | null;
}

export interface DossierArbitragePayload {
  objet_litige: string;
  partie_representee: Qualite;
  parties: PartieInput[];
  montant_demande: number;
  montant_devise?: string;
  convention_arbitrage: string;
  resume_pretentions: string;
  arbitres_proposes: ArbitreProposeInput[];
  siege?: string | null;
  langue?: string | null;
  droit_applicable?: string | null;
  souhaits_defaut?: string | null;
}

export interface DossierArbitrageResult {
  id: number;
  reference: string;
  statut: string;
  objet_litige: string;
  partie_representee: string;
  created_at: string;
}

export interface SoumissionResult {
  dossier_id: number;
  reference: string;
  statut: string;
  message: string;
}

export async function creerDossierArbitrage(
  payload: DossierArbitragePayload,
): Promise<DossierArbitrageResult> {
  const { data } = await apiClient.post<DossierArbitrageResult>(
    "/api/v1/avocat/dossiers",
    payload,
  );
  return data;
}

export async function televerserPiece(input: {
  dossier_id: number;
  categorie: string;
  file: File;
  bordereau_number?: string;
  obligatoire?: boolean;
}): Promise<AvocatPiece> {
  const form = new FormData();
  form.append("dossier_id", String(input.dossier_id));
  form.append("categorie", input.categorie);
  if (input.bordereau_number) form.append("bordereau_number", input.bordereau_number);
  if (input.obligatoire) form.append("obligatoire", "true");
  form.append("file", input.file);
  const { data } = await apiClient.post<AvocatPiece>(
    "/api/v1/avocat/pieces/upload",
    form,
    { timeout: 60_000 },
  );
  return data;
}

export async function soumettreDossier(
  dossierId: number,
): Promise<SoumissionResult> {
  const { data } = await apiClient.post<SoumissionResult>(
    `/api/v1/avocat/dossiers/${dossierId}/soumettre`,
  );
  return data;
}
